import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Info, X } from "lucide-react";
import {
  useInvestigationAdvice,
  useDeleteInvestigationAdviceItem,
} from "@/hooks/useInvestigationAdvice";
import { AddInvestigationDialog } from "@/components/consultation/dialogs/AddInvestigationDialog";
import { EmptyState } from "@/components/consultation/EmptyState";

interface InvestigationAdviceCardProps {
  appointmentId: string;
  isCompleted?: boolean;
}

export const InvestigationAdviceCard = ({
  appointmentId,
  isCompleted = false,
}: InvestigationAdviceCardProps) => {
  const { data: adviceList, isLoading } = useInvestigationAdvice(appointmentId);
  const deleteAdviceMutation = useDeleteInvestigationAdviceItem(appointmentId);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const activeAdvice = adviceList?.[0];
  const adviceItems = activeAdvice?.investigation_advice_items || [];

  return (
    <>
      <Card className="rounded-3xl border-border/60 bg-card shadow-sm">
        <CardHeader className="p-4 flex flex-row items-center justify-between border-b border-border/60">
          <div className="flex items-center gap-2">
            <CardTitle className="z-h3 text-foreground">
              Investigation Advice
            </CardTitle>
            <Info className="size-4 text-muted-foreground" />
          </div>
          {!isCompleted && (
            <Button
              size="icon"
              className="h-9 w-9 rounded-xl"
              onClick={() => setIsAddDialogOpen(true)}
            >
              <Plus className="size-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-4">
          {isLoading ? (
            <div className="z-body text-muted-foreground text-center py-6">
              Loading...
            </div>
          ) : adviceItems.length === 0 ? (
            <EmptyState
              title="No investigations advised yet"
              variant="compact"
            />
          ) : (
            <div className="space-y-3">
              {Object.entries(
                adviceItems.reduce(
                  (acc, item) => {
                    const type = item.item_type;
                    if (!acc[type]) acc[type] = [];
                    acc[type].push(item);
                    return acc;
                  },
                  {} as Record<string, typeof adviceItems>
                )
              ).map(([type, items]) => (
                <div
                  key={type}
                  className="rounded-xl border border-border/60 bg-muted/20 p-3"
                >
                  <div className="z-eyebrow text-muted-foreground mb-2">
                    {type.replace(/_/g, " ")}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full z-caption bg-muted text-foreground border border-border/60"
                      >
                        {item.item_name}
                        {!isCompleted && (
                          <button
                            className="p-0.5 rounded-full bg-destructive/10 hover:bg-destructive/20 transition-colors disabled:opacity-50"
                            onClick={() => deleteAdviceMutation.mutate(item.id)}
                            disabled={deleteAdviceMutation.isPending}
                          >
                            <X className="size-3 text-destructive" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AddInvestigationDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        appointmentId={appointmentId}
      />
    </>
  );
};
